import { Location } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';

// Models
import { Credentials } from 'capacitor-native-biometric';
import { isUUID } from 'src/app/platform/utils/functions';
import { ResponseData } from 'src/app/platform/models/utils';
import { AccountData, accountStatus, User } from 'src/app/modules/account/models/user';

// Services
import { UiService } from 'src/app/platform/services/ui.service';
import { UserService } from 'src/app/modules/account/user.service';
import { DeviceService } from 'src/app/platform/services/device.service';
import { AccountsService } from 'src/app/modules/account/accounts.service';

// Components
import { UiDialogComponent } from 'src/app/platform/components/ui-dialog/ui-dialog.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  public currentUser = new User();
  public user: string = '';
  public password: string = '';
  public screen: string;
  public response: any;
  public request: boolean;
  public queryParams: any;
  public passwordVisibility = false;

  public adminAccounts = new BehaviorSubject<string>('');
  public adminAccountsValue = 0;
  public adminAccountsLoading = false;
  public adminAccountsData: Array<AccountData> = [];
  public adminAccountsDataFiltered = this.adminAccounts.pipe(
    startWith(''),
    debounceTime(300),
    distinctUntilChanged(),
    switchMap(value => this.adminAccountsDataGet(value))
  );

  constructor(
    private router: Router,
    private location: Location,
    private uiDialog: MatDialog,
    private uiService: UiService,
    private userService: UserService,
    private deviceService: DeviceService,
    private activatedRoute: ActivatedRoute,
    private accountsService: AccountsService,
  ) {
    this.userService.currentUser.subscribe((userData) => {
      if(userData !== null){
        this.currentUser = userData;
        
        if(this.currentUser.id <= 0){
          this.screen = 'login';
          this.router.navigate(['ingreso']);
        }
      }
    })
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      this.queryParams = params;
    });

    if (this.userService.isLogged() && this.accountsService.isAccountSelected()) {
      this.router.navigate(['resumen']);
    } else {
      if (this.location.path().split('?')[0] === '/restablecer') {
        if (this.queryParams.token !== '' && typeof this.queryParams.token !== 'undefined'){
          this.screen = 'restore';
        } else {
          this.router.navigate(['ingreso']);
        }
      } else if (this.location.path().split('?')[0] === '/registro') {
        if (this.queryParams.token !== '' && typeof this.queryParams.token !== 'undefined'){
          this.screen = 'register';
        } else {
          this.router.navigate(['ingreso']);
        }
      } else if (this.location.path() === '/recuperar') {
        this.screen = 'recover';
      } else {
        this.screen = 'login';

        if (this.userService.isLogged() && !this.accountsService.isAccountSelected()) {
          this.onAccountCheck();
        } else {
          if (this.queryParams.from !== 'logout' || typeof this.queryParams.from === 'undefined'){
            //this.biometricAuth();
          }
        }
      }
    }
  }

  biometricAvailable: boolean = this.deviceService.biometricAvailable;
  
  biometricAuth(){
    this.deviceService.biometricGetCredentials().then((credentials: Credentials | false | undefined) => {
      console.log('biometricGetCredentials then', credentials);

      if (credentials !== undefined && credentials !== false) {
        this.user = credentials.username;
        this.password = credentials.password;

        this.onLogin();
      }
    }).catch(error => {
      console.log('biometricGetCredentials catch', error);
    });
  }

  setLocation(path: string): void{
    if (!this.request) {
      this.router.navigate([path]);
    }
  }

  onDevInfo(): void{
    this.uiDialog.open(UiDialogComponent, {
      width: '350px',
      data: {
        title: 'Desarrollado por Clousis',
        message: 'Sistema de autogestión IpTel 1.0.0<br>Clousis - <a href="https://www.clousis.com?ref=iptel-plataforma" target="_blank">www.clousis.com</a>',
        buttonCancelText: 'Cerrar',
        buttonConfirmText: ''
      }
    });
  }

  onLogin(): void{
    if (this.onLoginValidate()) {
      this.request = true;

      this.userService.login(this.user, this.password).then((response) => {
        this.request = false;

        if (response.status === true){
          this.deviceService.biometricSetCredentials(this.user, this.password).then(() => {
            if (!this.accountsService.isAccountSelected()) {
              this.onAccountCheck();
            } else {
              this.router.navigate(['resumen']);
            }
          });
        } else {
          this.response = {message: 'No es posible ingresar a la cuenta en este momento. Por favor intentá nuevamente más tarde.'};
        }
      }, (error) => {
        this.request = false;
        this.response = error;
      });
    }
  }

  onLoginValidate(): boolean{
    if (this.user && this.password) {
        this.response = {};
        return true;
    } else {
      this.response = {message: 'Es necesario completar todos los datos.'};
    }

    return false;
  }

  onRecover(): void{
    if (this.onRecoverValidate()) {
      this.request = true;

      this.userService.recover(this.user).then((response) => {
        this.request = false;

        if (response.status){
          this.uiService.showSnackBar('Te enviamos un correo para que puedas restablecer tu contraseña.');

          this.screen = 'login';
        } else {
          this.response = {message: 'No es posible restablecer la contraseña en este momento. Por favor intentá nuevamente más tarde.'};
        }
      }, (error) => {
        this.request = false;
        this.response = error;
      });
    }
  }

  onRecoverValidate(): boolean{
    if (this.user) {
        this.response = {};
        return true;
    } else {
      this.response = {message: 'Es necesario que ingreses la dirección de correo de tu cuenta.'};
    }

    return false;
  }

  onRestore(): void{
    const token = this.queryParams.token;

    if (token) {
      this.userService.restore(token).then((response) => {
        if (response.status){
          this.screen = 'login';
          this.request = false;
          this.uiService.showSnackBar('Te enviamos un correo con tu nueva contraseña.');
        } else {
          this.request = false;
          this.response = {message: 'No es posible generar una nueva contraseña en este momento. Por favor intentá nuevamente más tarde.'};
        }
      }, (error) => {
        this.request = false;
        this.response = error;
      });
    }
  }

  onActivate(): void{
    const token = this.queryParams.token;

    if (token) {
      this.accountsService.activate(token).then((response) => {
        if (response.status){
          this.screen = 'login';
          this.request = false;
          this.uiService.showSnackBar('Te enviamos un correo con tu contraseña.');
        } else {
          this.request = false;
          this.response = {message: 'No es posible activar tu cuenta en este momento. Por favor intentá nuevamente más tarde.'};
        }
      }, (error) => {
        this.request = false;
        this.response = error;
      });
    }
  }

  onAccountSelect(account: number): void{
    if (account) {
      this.uiService.showSpinner(true);

      this.accountsService.accountSet(account).then((status: boolean) => {
        this.uiService.showSpinner(false);

        if (this.accountsService.isAccountSelected()) {
          this.router.navigate(['']);
        } else {
          this.uiService.showSnackBar('No es posible administrar la cuenta en este momento (AC002).');
        }
      }, (error) => {
        this.uiService.showSpinner(false);
        this.uiService.showSnackBar(error);
      });
    } else {
      this.uiService.showSnackBar('No es posible administrar la cuenta en este momento (AC001).');
    }
  }

  onAccountCheck(): void{
    const accounts = this.accountsService.accountsGet();

    if (accounts.length === 1) {
      this.onAccountSelect(accounts[0].number);
    } else {
      this.screen = 'accounts';
    }
  }

  onTogglePassword(): void{
    this.passwordVisibility = !this.passwordVisibility;
  }

  accountStatus(status: string): string {
    return accountStatus(status);
  }

  // Admin Accounts

  adminAccountsChange(value: string) {
    this.adminAccounts.next(value);
  }

  adminAccountsDataDisplay(id: number){
    let response = '';

    if (this.adminAccountsData.length > 0) {
      if (this.adminAccountsData[0] instanceof AccountData) {
        response = this.adminAccountsData.find(item => item.number === id)?.name ?? 'error';
        this.adminAccountsValue = id;
      }
    }

    return response;
  }

  private adminAccountsDataGet(value: string): Promise<Array<AccountData>> {
    return new Promise((resolve, reject) => {
      // If the value of the field is not empty and if it is not a whole
      // uuid since when selecting a value the field is updated by the ID
      if (value !== '' && !isUUID(value)) {
        this.adminAccountsLoading = true;

        this.userService.accounts(value).then((response: ResponseData) => {
          this.adminAccountsLoading = false;
          this.adminAccountsData = response.result;

          resolve(this.adminAccountsData);
        }).catch(() => {
          this.adminAccounts.next('');
          this.adminAccountsLoading = false;

          resolve(this.adminAccountsData);
        });
      } else {
        resolve(this.adminAccountsData);
      }
    });
  }
}
